import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Database, Loader2, Play, RefreshCw, AlertCircle, Check } from 'lucide-react';
import Card from '../../components/Card';
import {
  getFileBackfillAssessment,
  runFileBackfill,
  getFileBackfillStatus,
} from './driveSettingsApi';

// Self-contained admin panel for the file-storage backfill. The run is a
// single-flight background job: clicking Run starts it server-side and returns
// immediately, and the panel polls for progress — so there's no long request to
// time out and no way to pile up concurrent runs.
function CoverageRow({ label, counts }) {
  if (!counts) return null;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '90px auto 1fr', gap: 10,
      padding: '8px 12px', alignItems: 'baseline',
      borderTop: '0.5px solid var(--border-primary)',
    }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>{label}</span>
      <span className="mono" style={{ fontSize: 13, color: 'var(--text-primary)' }}>
        {counts.migrated}/{counts.total} in DB
      </span>
      <span className="muted" style={{ fontSize: 11 }}>
        {counts.withNeither} orphan-risk · {counts.withLocalPath} local · {counts.withDriveFile} Drive
      </span>
    </div>
  );
}

export default function FileBackfillPanel() {
  const [assessment, setAssessment] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(''); // '' | 'assess'
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const refreshAssessment = useCallback(async () => {
    try { setAssessment(await getFileBackfillAssessment()); } catch (_) { /* non-fatal */ }
  }, []);

  // On mount: surface any in-flight/last run + current coverage (so a refresh
  // mid-run reconnects to the running job instead of looking idle).
  useEffect(() => {
    getFileBackfillStatus().then((r) => setJob(r.job || null)).catch(() => {});
    refreshAssessment();
  }, [refreshAssessment]);

  // Poll while a run is in progress; refresh coverage once it finishes.
  useEffect(() => {
    if (job?.status !== 'running') return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const r = await getFileBackfillStatus();
        setJob(r.job || null);
        if (r.job && r.job.status !== 'running') {
          clearInterval(pollRef.current);
          refreshAssessment();
        }
      } catch (_) { /* transient — keep polling */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [job?.status, refreshAssessment]);

  const assess = async () => {
    setBusy('assess'); setError('');
    try { setAssessment(await getFileBackfillAssessment()); }
    catch (e) { setError(e.message || 'Assess failed'); }
    finally { setBusy(''); }
  };

  const run = async () => {
    setError('');
    try {
      const r = await runFileBackfill();   // { started, job } — returns immediately
      setJob(r.job || null);               // the polling effect takes over
    } catch (e) { setError(e.message || 'Backfill failed'); }
  };

  const isRunning = job?.status === 'running';
  const counts = (j) => `${j.migrated} (${j.fromLocal} local, ${j.fromDrive} Drive)`
    + (j.orphanCount ? `, ${j.orphanCount} orphans` : '')
    + (j.errorCount ? `, ${j.errorCount} errors` : '');

  return (
    <Card title="File storage backfill" icon={Database}>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
        Copies historical file bytes into Postgres (the authoritative store) — local disk first,
        Google Drive as fallback. Runs as a <strong>single background job</strong> (one at a time),
        so you can start it and walk away; idempotent — safe to re-run. Files recoverable from neither
        source are reported as orphans. <strong>Assess</strong> is read-only. With the migration
        complete this is mainly a coverage check — confirm <em>migrated == total</em> with no orphans
        before retiring local copies.
      </div>

      {error && (
        <div className="callout red" style={{ alignItems: 'flex-start', marginBottom: 16 }}>
          <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
          <div className="callout-body" style={{ fontSize: 12 }}>{error}</div>
        </div>
      )}

      {assessment && (
        <div style={{
          border: 'var(--stroke-xs) solid var(--border-primary)',
          borderRadius: 'var(--corner-radius-md)', overflow: 'hidden', marginBottom: 16,
        }}>
          <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-tertiary)' }}>
            Coverage (already in DB / total)
          </div>
          <CoverageRow label="Contracts" counts={assessment.contractFiles} />
          <CoverageRow label="TPRM docs" counts={assessment.tprmDocuments} />
        </div>
      )}

      {job && (
        <div
          className={`callout ${job.status === 'error' ? 'red' : isRunning ? 'blue' : 'green'}`}
          style={{ alignItems: 'flex-start', marginBottom: 16 }}
        >
          <div className="callout-icon">
            {isRunning
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : job.status === 'error'
                ? <AlertCircle className="w-4 h-4" />
                : <Check className="w-4 h-4" />}
          </div>
          <div className="callout-body" style={{ fontSize: 12 }}>
            {isRunning && <>Running… processed <strong>{job.processed}</strong> · migrated {counts(job)}.</>}
            {job.status === 'done' && <>Done — migrated <strong>{job.migrated}</strong> file{job.migrated === 1 ? '' : 's'} ({job.fromLocal} local, {job.fromDrive} Drive){job.orphanCount ? `, ${job.orphanCount} orphans` : ''}{job.errorCount ? `, ${job.errorCount} errors` : ''}.</>}
            {job.status === 'error' && <>Backfill failed: {job.error}</>}
          </div>
        </div>
      )}

      <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={assess} disabled={!!busy || isRunning} className="btn outline sm">
          {busy === 'assess'
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <RefreshCw className="w-3.5 h-3.5" />}
          Assess
        </button>
        <button onClick={run} disabled={isRunning} className="btn primary sm">
          {isRunning
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running…</>
            : <><Play className="w-3.5 h-3.5" /> Run backfill</>}
        </button>
      </div>
    </Card>
  );
}
